import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';

import { FormsBuilderStateService } from '../services/forms-builder-state.service';
import { FormsBuilderPolishedService } from '../services/forms-builder-polished.service';
import { AiDraftApplyContext, FormsBuilderAiDraftService, PrebuiltTemplateGroup } from '../services/forms-builder-ai-draft.service';
import { FormsService } from '../../forms.service';
import { FormsGlobals } from '../../../../models/forms/forms.enum';
import { StaticDataService } from '../../../../services/static-data.service';

import { FormConfigurationModel } from '../../../../models/forms/form-configuration.model';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';
import { FormParagraphConfigurationModel } from '../../../../models/forms/form-paragraph-configuration.model';
import { FormItemConfigurationModel } from '../../../../models/forms/form-item-configuration.model';
import { FormMasterItemConfigurationModel } from '../../../../models/forms/form-master-item-configuration.model';
import { FormItemDataModel } from '../../../../models/forms/data/form-item-data.model';
import { FormsConstants, FormItemCategory, FormItemType } from '../../models/forms-constants';

import { FormsBuilderPolishedItemComponent } from '../forms-builder-polished-item/forms-builder-polished-item.component';
import { FormsBuilderPolishedConfirmComponent } from '../forms-builder-polished-confirm/forms-builder-polished-confirm.component';
import { FormsBuilderPolishedPreviewComponent } from '../forms-builder-polished-preview/forms-builder-polished-preview.component';
import { FormsBuilderTutorialComponent } from '../tutorial/forms-builder-tutorial.component';
import { FormsBuilderAiStudioDialogComponent } from '../forms-builder-ai-studio-dialog/forms-builder-ai-studio-dialog.component';
import {
  AiExistingParagraphContext,
  AiFormCatalogSnapshot,
  AiGeneratedFormDraft
} from '../models/ai-form-draft.model';

// Type definitions for drag-drop items
interface PrebuiltTemplate {
  name: string;
  label: string;
  controlType: string;
  icon: string;
  validValueName?: string;
  composite?: boolean;
  defaultValue?: string;
  labelWidth?: number;
  inputWidth?: number;
}

interface ItemTypeDefinition {
  id: string;
  name: string;
  icon: string;
}

interface CustomPrebuiltGroup {
  id: string;
  category: string;
  icon: string;
  fields: PrebuiltTemplate[];
  createdOn: string;
}

@Component({
  standalone: false,
  selector: 'app-paragraph-builder',
  templateUrl: './paragraph-builder.component.html',
  styleUrls: ['./paragraph-builder.component.scss']
})
export class ParagraphBuilderComponent implements OnInit, OnDestroy {
  TDOCID: number = 0;
  private isDestroyed = false;
  isLoading: boolean = true;
  isSaving: boolean = false;
  formIsEditable: boolean = false;
  canClickItems: boolean = true;

  // AI Studio commits its result into the in-memory version only; nothing reaches the backend
  // until saveFormData() runs. These drive the persistent "unsaved AI changes" banner, which
  // stays up until that save succeeds — leaving the editor first discards the whole draft.
  // 'importedVersion' is the finder's document import: the version is already persisted, but its
  // items have no master items until this editor saves it once.
  aiChangesAwaitingSave: boolean = false;
  aiChangesAwaitingSaveMode: 'import' | 'edit' | 'importedVersion' = 'edit';

  selectedCfgForm: FormConfigurationModel = null;
  selectedCfgFormVersion: FormVersionConfigurationModel = null;
  selectedCfgFormParagraph: FormParagraphConfigurationModel = null;
  selectedParagraphOrganizer: any[] = null;
  selectedParagraphId: string = null;

  connectedDragDropParagraphs: string[] = [];

  activeFormVersionStatus: string = '';
  activeParagraphOrientation: string = '';
  activeParagraphItemLayout: string = '';

  validValues: any[] = [];
  actionList: any[] = [];
  cfgFormItemTypeList: FormItemType[] = [];

  // New categorization properties
  categorizedItems: Map<FormItemCategory, FormItemType[]> = new Map();
  filteredCategorizedItems: Map<FormItemCategory, FormItemType[]> = new Map();
  recentlyUsedItems: FormItemType[] = [];

  // Legacy properties (keep for backward compatibility with prebuilt templates)
  consentFormItems: any[] = [];
  otherItems: any[] = [];
  filteredConsentFormItems: any[] = [];
  filteredOtherItems: any[] = [];

  cfgFormParagraphOrientationList: any[] = [];
  cfgFormItemLayoutList: any[] = [];
  cfgFormVersionStatusList: any[] = [];

  guidList: string[] = [];

  // Track if revision has been incremented this session (to avoid multiple increments)
  private revisionIncrementedThisSession: boolean = false;

  // Live Preview Mode (replaces old side-panel preview). Fixed on: the top-bar control that
  // toggled it back to card mode was removed, so the builder always renders items WYSIWYG.
  livePreviewMode: boolean = true;
  livePreviewEnabled: boolean = false;
  livePreviewCfgFormVersion: FormVersionConfigurationModel = null;
  previewUpdateTimer: any;

  // Center pane mode. Defaults to 'preview' so opening a version shows the whole rendered form
  // straight away — the editor used to land on a "No Paragraph Selected" empty state, so the user
  // saw nothing of their form until they clicked a paragraph.
  centerMode: 'preview' | 'edit' = 'preview';

  // Render source for the embedded whole-form preview. Held separately from
  // selectedCfgFormVersion because the preview component only re-renders when this input's
  // *reference* changes (see refreshCenterPreview).
  previewFormVersion: FormVersionConfigurationModel = null;

  // View controls for the embedded preview, hoisted out of the preview component's own control strip
  // so the editor has a single header row. Defaults must match the preview component's own defaults.
  previewUseDocumentFrame: boolean = true;
  previewMobile: boolean = false;

  // Live preview builder state
  selectedItemId: string = null;
  hoveredItem: FormItemConfigurationModel = null;
  private spoofDataCache = new Map<string, FormItemDataModel>();
  private spoofDataDirty: boolean = true;
  builderNoopEmitter = new EventEmitter<any>();

  // UI state
  paragraphTabIndex: number = 0;
  itemSearchQuery: string = '';
  consentFormGroupExpanded: boolean = true;
  otherGroupExpanded: boolean = true;

  // Expanded text editing state
  expandedItemId: string = null;

  private consentFormItemIds = [
    '00000001-1000-0000-0000-000000000007',
    '00000001-1000-0000-0000-000000000011',
    '00000001-1000-0000-0000-000000000002',
    '00000001-1000-0000-0000-000000000006',
    '00000001-1000-0000-0000-000000000004',
    '00000001-1000-0000-0000-000000000009',
    '00000001-1000-0000-0000-000000000028',
    '00000001-1000-0000-0000-000000000027',
    '00000001-1000-0000-0000-000000000026',
    '00000001-1000-0000-0000-000000000001',
  ];

  // Control type IDs that support inline editing
  private inlineEditableControlTypes = [
    '00000001-1000-0000-0000-000000000002', // Heading
    '00000001-1000-0000-0000-000000000018', // Subheading
    '00000001-1000-0000-0000-000000000006', // Label
    '00000001-1000-0000-0000-000000000001', // TextBox
    '00000001-1000-0000-0000-000000000025', // TextArea
  ];

  private multilineControlTypes = [
    '00000001-1000-0000-0000-000000000025', // TextArea
  ];

  // Prebuilt field templates — sourced from FormsBuilderAiDraftService (single source of truth,
  // shared with the form finder's PDF-import flow). Assigned in the constructor.
  prebuiltTemplates: PrebuiltTemplateGroup[];

  quickAddExpanded: boolean = true;

  // Custom prebuilt templates (saved by user)
  customTemplateGroups: CustomPrebuiltGroup[] = [];

  private routeSubscription: Subscription;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private stateService: FormsBuilderStateService,
    private builderService: FormsBuilderPolishedService,
    private formsService: FormsService,
    private globals: FormsGlobals,
    private staticDataService: StaticDataService,
    private aiDraftService: FormsBuilderAiDraftService
  ) {
    this.prebuiltTemplates = this.aiDraftService.prebuiltTemplateGroups;
  }

  ngOnInit(): void {
    this.TDOCID = parseInt(localStorage.getItem('TDOCID') || '0', 10);

    if (this.staticDataService.isReady) {
      this.setupEnums();
    } else {
      this.subscriptions.add(
        this.staticDataService.validValuesReadyEvent.subscribe({
          complete: () => {
            this.setupEnums();
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.subscriptions.unsubscribe();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.previewUpdateTimer) {
      clearTimeout(this.previewUpdateTimer);
    }
  }

  setupEnums(): void {
    this.setupStatusList();
    this.setupParagraphOrientationList();
    this.setupItemLayoutList();
    this.setupItemTypeList();
    this.setupAllFieldsForItemConfig();
    this.loadFormAndVersion();
  }

  setupStatusList(): void {
    // Note: "Active" and "Inactive" are computed display statuses in the form finder
    // These are the editable statuses for the form builder
    this.cfgFormVersionStatusList = [
      { name: 'Approved', id: this.globals.FORM_STATUS_APPROVED },
      { name: 'Draft', id: this.globals.FORM_STATUS_DRAFT },
      { name: 'Ready for review', id: this.globals.FORM_STATUS_READY_FOR_REVIEW },
      { name: 'In review', id: this.globals.FORM_STATUS_IN_REVIEW }
    ];
  }

  setupParagraphOrientationList(): void {
    this.cfgFormParagraphOrientationList = [
      { name: 'Horizontal', id: this.globals.PARAGRAPH_LAYOUT_HORIZONTAL },
      { name: 'Vertical', id: this.globals.PARAGRAPH_LAYOUT_VERTICAL },
    ];
  }

  setupItemLayoutList(): void {
    this.cfgFormItemLayoutList = [
      { name: 'Side by side', id: this.globals.ITEM_LAYOUT_SBS },
      { name: 'Two line', id: this.globals.ITEM_LAYOUT_TWO_LINE },
    ];
  }

  setupItemTypeList(): void {
    const allItems = FormsConstants.cfgFormItemTypeList;

    // New categorization: Group by semantic category
    const grouped = new Map<FormItemCategory, FormItemType[]>();

    Object.values(FormItemCategory).forEach(category => {
      const items = allItems
        .filter(item => item.category === category)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      if (items.length > 0) {
        grouped.set(category, items);
      }
    });

    this.categorizedItems = grouped;
    this.filteredCategorizedItems = grouped;

    // Legacy: Keep old consent/other categorization for backward compatibility
    this.consentFormItems = allItems
      .filter(item => this.consentFormItemIds.includes(item.id))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    this.otherItems = allItems
      .filter(item => !this.consentFormItemIds.includes(item.id))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    this.cfgFormItemTypeList = allItems;
    this.filteredConsentFormItems = this.consentFormItems;
    this.filteredOtherItems = this.otherItems;

    // Load recently used items from localStorage
    this.loadRecentlyUsed();

    // Load custom prebuilt templates from localStorage
    this.loadCustomTemplates();
  }

  loadRecentlyUsed(): void {
    const stored = localStorage.getItem('paragraphBuilder.recentlyUsed');
    if (stored) {
      try {
        const ids = JSON.parse(stored) as string[];
        this.recentlyUsedItems = ids
          .map(id => FormsConstants.cfgFormItemTypeList.find(item => item.id === id))
          .filter(item => !!item)
          .slice(0, 5); // Keep only last 5
      } catch (e) {
        console.error('Failed to load recently used items:', e);
        this.recentlyUsedItems = [];
      }
    }
  }

  trackItemUsage(item: FormItemType): void {
    // Add to recently used (remove if exists, add to front)
    const filtered = this.recentlyUsedItems.filter(i => i.id !== item.id);
    this.recentlyUsedItems = [item, ...filtered].slice(0, 5);

    // Save to localStorage
    const ids = this.recentlyUsedItems.map(i => i.id);
    localStorage.setItem('paragraphBuilder.recentlyUsed', JSON.stringify(ids));
  }

  setupAllFieldsForItemConfig(): void {
    this.canClickItems = false;
    const promises = [
      this.fetchAllValidValues(),
      this.fetchAllActions(),
    ];

    Promise.all(promises)
      .then((results: any[]) => {
        this.validValues = results[0].sort((a, b) =>
          a.ValidValueName.toLowerCase().localeCompare(b.ValidValueName.toLowerCase())
        );
        this.actionList = results[1].sort((a, b) =>
          a.ActionName.toLowerCase().localeCompare(b.ActionName.toLowerCase())
        );
        // Items can render before valid values arrive (form already in state service), which caches
        // spoof data without ValidValue and leaves radio/checkbox lists empty. Rebuild it now.
        // clear() rather than invalidateSpoofCache(): its dirty window hands every binding a new
        // object on each check, which throws ExpressionChangedAfterItHasBeenChecked in dev mode.
this.spoofDataCache.clear();
this.spoofDataDirty = false;
        this.canClickItems = true;
      })
      .catch(err => {
        this.canClickItems = true;
        this.showMessage('Error', 'Error loading configurations');
      });
  }

  loadFormAndVersion(): void {
    const formId = this.route.snapshot.paramMap.get('formId');
    const versionId = this.route.snapshot.paramMap.get('versionId');
    const mode = this.route.snapshot.paramMap.get('mode');
    this.formIsEditable = mode === 'edit';

    // Try to get from state service first
    const stateForm = this.stateService.selectedForm;
    const stateVersion = this.stateService.selectedVersion;

    if (stateForm && stateForm.DFormId === formId) {
      this.selectedCfgForm = stateForm;

      if (stateVersion && stateVersion.DFormVersionId === versionId) {
        this.selectedCfgFormVersion = stateVersion;
        this.setupVersionData();
        this.isLoading = false;
      } else {
        // Find version in form
        const version = stateForm.cfgDFormVersions?.find(v => v.DFormVersionId === versionId);
        if (version) {
          this.selectedCfgFormVersion = version;
          this.stateService.setSelectedVersion(version);
          this.setupVersionData();
          this.isLoading = false;
        } else {
          this.loadFromApi(formId, versionId);
        }
      }
    } else {
      this.loadFromApi(formId, versionId);
    }
  }

  loadFromApi(formId: string, versionId: string): void {
    this.formsService.getCfgDForm(formId)
      .subscribe(
        (form: FormConfigurationModel) => {
          this.selectedCfgForm = form;
          this.stateService.setSelectedForm(form);

          const version = form.cfgDFormVersions?.find(v => v.DFormVersionId === versionId);
          if (version) {
            this.selectedCfgFormVersion = version;
            this.stateService.setSelectedVersion(version);
            this.setupVersionData();
          } else {
            this.showMessage('Error', 'Version not found');
            this.stateService.navigateToFinder();
          }
          this.isLoading = false;
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
        },
        err => {
          this.showMessage('Error', 'Error loading form');
          this.stateService.navigateToFinder();
          this.isLoading = false;
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
        }
      );
  }

  setupVersionData(): void {
    if (this.selectedCfgFormVersion.enumDFormBuildStatus) {
      this.activeFormVersionStatus = this.selectedCfgFormVersion.enumDFormBuildStatus.ItemDisplayValue;
    }
    this.selectedCfgFormVersion.cfgDFormParagraphs = this.selectedCfgFormVersion.cfgDFormParagraphs
      .filter(p => !p.xauditDeleted)
      .sort((a, b) => a.ParagraphOrder - b.ParagraphOrder);

    this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(paragraph => {
      paragraph.cfgDFormItems = paragraph.cfgDFormItems.filter(item => !item.xauditDeleted);
    });

    // Opened straight from the finder's document import: raise the save banner. Consumed even in
    // view mode so a stale flag can't resurface on a later visit.
    if (this.stateService.consumePendingImportSave(this.selectedCfgFormVersion.DFormVersionId) && this.formIsEditable) {
      this.aiChangesAwaitingSave = true;
      this.aiChangesAwaitingSaveMode = 'importedVersion';
    }

    // Populate the center preview for first paint — the editor opens in preview mode.
    this.refreshCenterPreview();
  }

  goBack(): void {
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }
    this.stateService.setSelectedForm(this.selectedCfgForm);
    this.stateService.setSelectedVersion(this.selectedCfgFormVersion);
    this.stateService.navigateToFinder();
  }

  // Paragraph methods
  selectDFormParagraph(paragraph: FormParagraphConfigurationModel): void {
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }
    // Picking a paragraph is intent to edit it — leave the whole-form preview.
    this.centerMode = 'edit';
    this.selectedParagraphId = paragraph.DFormParagraphId;
    this.selectedCfgFormParagraph = paragraph;
    this.organizeParagraph(this.selectedCfgFormParagraph);
    this.activeParagraphOrientation = this.selectedCfgFormParagraph.enumParagraphOrientationID;
    this.activeParagraphItemLayout = this.selectedCfgFormParagraph.enumItemLayoutID;
    this.syncLivePreview();
  }

  organizeParagraph(paragraph: FormParagraphConfigurationModel): void {
    this.selectedParagraphOrganizer = [];
    const itemOrganizer: { [key: number]: FormItemConfigurationModel[] } = {};
    const paragraphClone = JSON.parse(JSON.stringify(paragraph));
    paragraphClone.cfgDFormItems = paragraph.cfgDFormItems.filter(item => !item.xauditDeleted);

    paragraphClone.cfgDFormItems.forEach((item: FormItemConfigurationModel) => {
      if (itemOrganizer[item.ParagraphRow]) {
        itemOrganizer[item.ParagraphRow].push(item);
      } else {
        itemOrganizer[item.ParagraphRow] = [item];
      }
    });

    const itemArray: FormItemConfigurationModel[][] = [];
    for (const i in itemOrganizer) {
      itemOrganizer[i] = itemOrganizer[i].sort((a, b) => a.ParagraphColumn - b.ParagraphColumn);
      itemArray.push(itemOrganizer[i]);
    }

    this.selectedParagraphOrganizer = itemArray;
    this.rebuildConnectedDropLists();
  }

  createNewDFormParagraph(): void {
    const newParagraph = new FormParagraphConfigurationModel();
    newParagraph.DFormVersionId = this.selectedCfgFormVersion.DFormVersionId;
    newParagraph.xauditDeleted = false;
    newParagraph.cfgDFormItems = [];
    newParagraph.ParagraphName = 'New Paragraph';

    let lastOrder = 0;
    this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(p => {
      if (p.ParagraphOrder > lastOrder) {
        lastOrder = p.ParagraphOrder;
      }
    });
    newParagraph.ParagraphOrder = lastOrder + 1;

    this.getNewGuids(1)
      .then((guids: string[]) => {
        newParagraph.DFormParagraphId = guids[0];
        this.selectedCfgFormVersion.cfgDFormParagraphs.push(newParagraph);
        this.selectDFormParagraph(newParagraph);
        this.syncLivePreview();
      })
      .catch(err => {
        console.error('Failed to generate GUID for paragraph:', err);
        this.showMessage('Error', 'Failed to create paragraph. Please try again.');
      });
  }

  deleteParagraph(): void {
    this.confirm('Are you sure you want to delete this paragraph?')
      .then(result => {
        if (!result) return;

        const index = this.selectedCfgFormVersion.cfgDFormParagraphs.findIndex(
          p => p.DFormParagraphId === this.selectedCfgFormParagraph.DFormParagraphId
        );

        if (index >= 0) {
          const paragraph = this.selectedCfgFormVersion.cfgDFormParagraphs[index];
          this.selectedCfgFormVersion.cfgDFormParagraphs.splice(index, 1);
          paragraph.xauditDeleted = true;

          this.saveParagraph(paragraph)
            .then(() => {
              this.selectedCfgFormParagraph = null;
              this.selectedParagraphId = null;
              this.selectedParagraphOrganizer = null;
              this.activeParagraphOrientation = null;
              this.activeParagraphItemLayout = null;
              this.syncLivePreview();
              this.showMessage('Success', 'Paragraph deleted');
            });
        }
      });
  }

  paragraphReOrderEvent(event: CdkDragDrop<any[]>, items: FormParagraphConfigurationModel[]): void {
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    items.forEach((item, i) => {
      item.ParagraphOrder = i;
    });
    this.syncLivePreview();
  }

  selectParagraphOrientation(orientationId: string): void {
    this.selectedCfgFormParagraph.enumParagraphOrientationID = orientationId;
    this.syncLivePreview();
  }

  selectParagraphItemLayout(layoutId: string): void {
    this.selectedCfgFormParagraph.enumItemLayoutID = layoutId;
    this.syncLivePreview();
  }

  onParagraphNameChange(): void {
    this.syncLivePreview();
  }

  // Row management
  dropEventOnRow(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return; // No change needed
    }
    moveItemInArray(this.selectedParagraphOrganizer, event.previousIndex, event.currentIndex);
    this.updateRowIndices();
    this.rebuildConnectedDropLists();
    this.syncLivePreview();
  }

  dropEventInRow(event: CdkDragDrop<any[]>, row: any[], rowIndex: number): void {
    // Use the actual row from the organizer array to avoid stale references
    const actualRow = this.selectedParagraphOrganizer[rowIndex];

    if (event.previousContainer === event.container) {
      // Reordering within the same row
      if (event.previousIndex !== event.currentIndex) {
        moveItemInArray(actualRow, event.previousIndex, event.currentIndex);
      }
    } else {
      // Use event.item.data when available (set via cdkDragData), otherwise fallback to container index
      const itemToMove = event.item.data ?? event.previousContainer.data[event.previousIndex];

      if (this.isPrebuiltTemplate(itemToMove)) {
        // Prebuilt template being dragged
        this.addPrebuiltItemToRow(actualRow, itemToMove, rowIndex, event.currentIndex);
        return; // addPrebuiltItemToRow handles formatting and sync
      } else if (this.isItemTypeDefinition(itemToMove)) {
        // New item from library
        this.addEmptyItemIntoRow(actualRow, itemToMove, rowIndex);
      } else if (this.isFormItem(itemToMove)) {
        // Existing form item being moved between rows
        const sourceContainerId = event.previousContainer.id;
        const sourceRowIndex = this.getRowIndexFromContainerId(sourceContainerId);

        if (sourceRowIndex !== -1) {
          const sourceRow = this.selectedParagraphOrganizer[sourceRowIndex];
          transferArrayItem(sourceRow, actualRow, event.previousIndex, event.currentIndex);
        } else {
          transferArrayItem(event.previousContainer.data, actualRow, event.previousIndex, event.currentIndex);
        }
      }
    }
    this.formatFormItemsFromOrganizer();
    this.syncLivePreview();
  }

  private getRowIndexFromContainerId(containerId: string): number {
    if (containerId && containerId.startsWith('paragraphRow')) {
      const indexStr = containerId.replace('paragraphRow', '');
      const index = parseInt(indexStr, 10);
      if (!isNaN(index) && index >= 0 && index < this.selectedParagraphOrganizer.length) {
        return index;
      }
    }
    return -1;
  }

  // Type guards for drag-drop item detection
  private isPrebuiltTemplate(item: any): item is PrebuiltTemplate {
    return item &&
      typeof item.controlType === 'string' &&
      typeof item.label === 'string' &&
      typeof item.name === 'string';
  }

  private isItemTypeDefinition(item: any): item is ItemTypeDefinition {
    return item &&
      typeof item.id === 'string' &&
      typeof item.icon === 'string' &&
      !item.controlType;
  }

  private isFormItem(item: any): item is FormItemConfigurationModel {
    return item && typeof item.DFormItemId === 'string';
  }

  // TrackBy functions for stable *ngFor rendering during drag operations
  trackByRowIndex(index: number, row: any[]): number {
    return index;
  }

  trackByItemId(index: number, item: FormItemConfigurationModel): string {
    return item.DFormItemId || `item-${index}`;
  }

  addParagraphRowToOrganizer(): void {
    this.selectedParagraphOrganizer.push([]);
    this.rebuildConnectedDropLists();
    this.syncLivePreview();
  }

  insertRowAt(index: number): void {
    if (!this.selectedParagraphOrganizer) {
      this.selectedParagraphOrganizer = [];
    }
    this.selectedParagraphOrganizer.splice(index, 0, []);
    this.updateRowIndices();
    this.rebuildConnectedDropLists();
    this.syncLivePreview();
  }

  deleteRowAt(index: number): void {
    if (this.selectedParagraphOrganizer[index].length > 0) {
      this.showMessage('Error', 'Cannot delete a row with items');
      return;
    }

    this.confirm('Are you sure you want to delete this row?')
      .then(result => {
        if (result) {
          this.selectedParagraphOrganizer.splice(index, 1);
          this.updateRowIndices();
          this.rebuildConnectedDropLists();
          this.syncLivePreview();
          this.showMessage('Success', 'Row deleted');
        }
      });
  }

  private updateRowIndices(): void {
    this.selectedParagraphOrganizer.forEach((row, i) => {
      row.forEach((item: FormItemConfigurationModel) => {
        item.ParagraphRow = i;
      });
    });
  }

  private rebuildConnectedDropLists(): void {
    this.connectedDragDropParagraphs = [];
    for (let i = 0; i < this.selectedParagraphOrganizer.length; i++) {
      this.connectedDragDropParagraphs.push(`paragraphRow${i}`);
    }
    this.connectedDragDropParagraphs.push('itemListDropList');
    if (!this.hasOnlyStarterEmptyRow()) {
      this.connectedDragDropParagraphs.push('autoCreateRow');
    }
    this.connectedDragDropParagraphs.push('prebuiltDropList');
  }

  hasOnlyStarterEmptyRow(): boolean {
    return !!this.selectedParagraphOrganizer
      && this.selectedParagraphOrganizer.length === 1
      && this.selectedParagraphOrganizer[0]?.length === 0;
  }

  shouldShowAutoRowDropZone(): boolean {
    return this.formIsEditable
      && !!this.selectedParagraphOrganizer
      && this.selectedParagraphOrganizer.length > 0
      && !this.hasOnlyStarterEmptyRow();
  }

  // Item methods
  addEmptyItemIntoRow(row: any[], itemType: ItemTypeDefinition, rowIndex: number): void {
    const newItem = new FormItemConfigurationModel();
    newItem.DFormParagraphId = this.selectedCfgFormParagraph.DFormParagraphId;
    newItem.enumFormControlID = itemType.id;
    newItem.xauditDeleted = false;
    newItem.DFormItemName = itemType.name || 'New Item';

    // Track usage for recently used list
    const fullItemType = FormsConstants.cfgFormItemTypeList.find(item => item.id === itemType.id);
    if (fullItemType) {
      this.trackItemUsage(fullItemType);
    }

    this.getNewGuids(1)
      .then((guids: string[]) => {
        newItem.DFormItemId = guids[0];
        row.push(newItem);
        this.rebuildConnectedDropLists();
        this.openItemDetailDetail(newItem, rowIndex, row.length - 1);
      })
      .catch(err => {
        console.error('Failed to generate GUID for item:', err);
        this.showMessage('Error', 'Failed to add item. Please try again.');
      });
  }

  formatFormItemsFromOrganizer(): void {
    for (let i = 0; i < this.selectedParagraphOrganizer.length; i++) {
      const row = this.selectedParagraphOrganizer[i];
      for (let j = 0; j < row.length; j++) {
        const item: FormItemConfigurationModel = row[j];
        item.ParagraphRow = i;
        item.ParagraphColumn = j;
        item.ItemOrder = j;
      }
    }
  }

  openItemDetailDetail(item: FormItemConfigurationModel, rowIndex: number = -1, itemIndex: number = -1): Promise<any> {
    if (!this.canClickItems) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(FormsBuilderPolishedItemComponent, {
        width: '1000px',
        height: '750px',
        data: {
          cfgItem: item,
          otherItems: this.returnAllItemsForForm(),
          validValues: this.validValues,
          actionList: this.actionList,
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.delete) {
          this.handleItemDelete(item, rowIndex, itemIndex);
          resolve(false);
        } else if (result) {
          this.selectedParagraphOrganizer[rowIndex][itemIndex] = JSON.parse(JSON.stringify(result.item));
          if (result.duplicate) {
            this.duplicateItem(result.item, rowIndex);
          }
          this.syncLivePreview();
          resolve(item);
        } else {
          resolve(null);
        }
      }, err => reject(err));
    });
  }

  handleItemDelete(item: FormItemConfigurationModel, rowIndex: number, itemIndex: number): void {
    // Sync organizer state to cfgDFormItems first
    this.overwriteParagraphItems();

    // Remove from organizer row
    if (this.selectedParagraphOrganizer[rowIndex]) {
      this.selectedParagraphOrganizer[rowIndex].splice(itemIndex, 1);
    }

    // Mark as deleted in cfgDFormItems (for backend persistence)
    this.selectedCfgFormParagraph.cfgDFormItems.forEach(cfgItem => {
      if (cfgItem.DFormItemId === item.DFormItemId) {
        cfgItem.xauditDeleted = true;
        cfgItem.ParagraphColumn = 99999;
        cfgItem.ItemOrder = 99999;
      }
    });

    // Reassign row/column from the organizer (single source of truth)
    this.formatFormItemsFromOrganizer();

    // Sync back and refresh
    this.overwriteParagraphItems();
    this.saveItem(item, this.selectedCfgForm);
    this.syncLivePreview();
    this.showMessage('Success', 'Item deleted');
  }

  deleteItemFromRow(item: FormItemConfigurationModel, rowIndex: number, itemIndex: number): void {
    if (!this.formIsEditable) return;

    this.confirm('Are you sure you want to delete this item?')
      .then(result => {
        if (result) {
          this.handleItemDelete(item, rowIndex, itemIndex);
        }
      });
  }

  duplicateItem(itemToDuplicate: FormItemConfigurationModel, rowIndex: number): void {
    const newItem: FormItemConfigurationModel = JSON.parse(JSON.stringify(itemToDuplicate));
    newItem.cfgDFormMasterItem = null;
    newItem.DFormMasterItemId = null;

    this.getNewGuids(1)
      .then((guids: string[]) => {
        newItem.DFormItemId = guids[0];
        newItem.DFormItemName += guids[0].slice(0, 5);
        newItem.ParentDFormItemId = null;
        this.selectedParagraphOrganizer[rowIndex].push(newItem);
        this.syncLivePreview();
      })
      .catch(err => {
        console.error('Failed to generate GUID for duplicate item:', err);
        this.showMessage('Error', 'Failed to duplicate item. Please try again.');
      });
  }

  duplicateParagraph(paragraph?: FormParagraphConfigurationModel): void {
    const source = paragraph || this.selectedCfgFormParagraph;
    if (!source) return;

    // Sync current organizer items back to the selected paragraph before cloning
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }

    // Build item list: filter out deleted items and ensure we have valid data to clone
    const sourceItems = (source.cfgDFormItems || []).filter(item => !item.xauditDeleted);

    const newParagraph: FormParagraphConfigurationModel = JSON.parse(JSON.stringify(source));
    newParagraph.cfgDFormItems = JSON.parse(JSON.stringify(sourceItems));
    const suffix = ' (Copy)';
    const baseName = source.ParagraphName || '';
    newParagraph.ParagraphName = (baseName.length + suffix.length > 50
      ? baseName.substring(0, 50 - suffix.length)
      : baseName) + suffix;

    const itemCount = newParagraph.cfgDFormItems.length;
    const totalGuids = 1 + itemCount;

    this.getNewGuids(totalGuids)
      .then((guids: string[]) => {
        let guidIndex = 0;
        newParagraph.DFormParagraphId = guids[guidIndex++];
        newParagraph.DFormVersionId = this.selectedCfgFormVersion.DFormVersionId;

        const itemIdMap = new Map<string, string>();
        if (newParagraph.cfgDFormItems.length > 0) {
          newParagraph.cfgDFormItems = newParagraph.cfgDFormItems.map(item => {
            const oldId = item.DFormItemId;
            const newId = guids[guidIndex++];
            itemIdMap.set(oldId, newId);
            item.DFormItemId = newId;
            item.DFormParagraphId = newParagraph.DFormParagraphId;
            item.DFormMasterItemId = null;
            item.cfgDFormMasterItem = null;
            return item;
          });
        }

        if (source.RepeatDFormItemId) {
          const newRepeatId = itemIdMap.get(source.RepeatDFormItemId);
          if (newRepeatId) {
            newParagraph.RepeatDFormItemId = newRepeatId;
            newParagraph.RepeatDFormItem = newParagraph.cfgDFormItems.find(
              i => i.DFormItemId === newRepeatId
            ) ?? null;
          } else {
            newParagraph.RepeatDFormItemId = null;
            newParagraph.RepeatDFormItem = null;
          }
        }

        newParagraph.cfgDFormItems.forEach(item => {
          if (item.ParentDFormItemId) {
            item.ParentDFormItemId = itemIdMap.get(item.ParentDFormItemId) ?? null;
          }
        });

        newParagraph.ParagraphOrder = (source.ParagraphOrder || 0) + 1;

        this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(p => {
          if (p.DFormParagraphId !== source.DFormParagraphId && p.ParagraphOrder > source.ParagraphOrder) {
            p.ParagraphOrder++;
          }
        });

        const sourceIndex = this.selectedCfgFormVersion.cfgDFormParagraphs.indexOf(source);
        this.selectedCfgFormVersion.cfgDFormParagraphs.splice(sourceIndex + 1, 0, newParagraph);

        this.selectDFormParagraph(newParagraph);
        this.syncLivePreview();
      })
      .catch(err => {
        console.error('Failed to generate GUIDs for duplicate paragraph:', err);
        this.showMessage('Error', 'Failed to duplicate paragraph. Please try again.');
      });
  }

  returnAllItemsForForm(): FormItemConfigurationModel[] {
    const itemArray: FormItemConfigurationModel[] = [];
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }
    this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(paragraph => {
      paragraph.cfgDFormItems.forEach(item => {
        if (!item.xauditDeleted) {
          itemArray.push(item);
        }
      });
    });
    return itemArray;
  }

  getItemLabel(cfgItem: FormItemConfigurationModel): any {
    const itemLabel = this.cfgFormItemTypeList.find(itemType => itemType.id === cfgItem.enumFormControlID);
    return itemLabel || { name: '', icon: '' };
  }

  // Save methods
  saveFormData(): void {
    if (!this.formIsEditable) { return; }
    if (this.isSaving) { return; }
    this.isSaving = true;
    this.isLoading = true;

    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }

    // Increment revision on first save of this editing session
    if (this.formIsEditable && !this.revisionIncrementedThisSession) {
      this.selectedCfgFormVersion.Revision += 1;
      this.revisionIncrementedThisSession = true;
    }

    this.saveForm(this.selectedCfgForm)
      .then((savedForm: FormConfigurationModel) => {
        if (savedForm?.xauditModifiedOn) {
          this.selectedCfgForm.xauditModifiedOn = savedForm.xauditModifiedOn;
        }
        return this.saveVersion(this.selectedCfgFormVersion);
      })
      .then((savedVersion: FormVersionConfigurationModel) => {
        if (savedVersion?.xauditModifiedOn) {
          this.selectedCfgFormVersion.xauditModifiedOn = savedVersion.xauditModifiedOn;
          const versionInForm = this.selectedCfgForm.cfgDFormVersions?.find(
            v => v.DFormVersionId === this.selectedCfgFormVersion.DFormVersionId
          );
          if (versionInForm) {
            versionInForm.xauditModifiedOn = savedVersion.xauditModifiedOn;
          }
        }
        const paragraphPromises = this.selectedCfgFormVersion.cfgDFormParagraphs.map(p => this.saveParagraph(p));
        return Promise.all(paragraphPromises);
      })
      .then(() => {
        const itemPromises: Promise<any>[] = [];
        this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(paragraph => {
          paragraph.cfgDFormItems.forEach(item => {
            itemPromises.push(this.saveItem(item, this.selectedCfgForm));
          });
        });
        return Promise.all(itemPromises);
      })
      .then(() => {
        this.isLoading = false;
        this.isSaving = false;
        // Cleared BEFORE detectChanges: the save has already succeeded by this point, so a render
        // error must not roll the UI back into "unsaved" and report a failure that did not happen.
        // The catch below deliberately leaves the banner up, since a genuinely failed save means
        // the AI changes are still unpersisted.
        this.aiChangesAwaitingSave = false;
        this.showMessage('Success', 'Data saved');
        // Data is already persisted: post-save UI bookkeeping must never reach the
        // "Error saving data" catch below. The tombstone strip and state sync get their own try and
        // run before the render, so a render error can't skip them and leave deleted items in
        // cfgDFormItems to be posted again on the next save.
        try {
          this.filterOutAllDeletedObjects();
          this.stateService.setSelectedForm(this.selectedCfgForm);
          this.stateService.setSelectedVersion(this.selectedCfgFormVersion);
        } catch (e) {
          console.error('Post-save state sync failed (save succeeded):', e);
        }
        try {
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          // Tombstones were stripped above, so a preview built before the save is now stale.
          this.refreshCenterPreview();
        } catch (e) {
          console.error('Post-save refresh failed (save succeeded):', e);
        }
      })
      .catch(err => {
        this.isLoading = false;
        this.isSaving = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error saving data');
        console.error(err);
      });
  }

  /**
   * Syncs the organizer back into the selected paragraph's cfgDFormItems.
   *
   * Soft-deleted items MUST survive this round-trip. Deletion is persisted by tombstone, not by
   * removal: saveFormData() POSTs every item in cfgDFormItems, and an item carrying
   * xauditDeleted:true is how the backend learns to delete it. organizeParagraph() strips
   * tombstones out of the organizer (they must not render), so rebuilding the array from the
   * organizer alone silently drops those pending deletions — the item then survives the save and
   * reappears on reload stacked on top of whatever replaced it (e.g. an AI in-place edit).
   */
  overwriteParagraphItems(): void {
    if (!this.selectedCfgFormParagraph) return;

    this.selectedCfgFormVersion.cfgDFormParagraphs.forEach(paragraph => {
      if (paragraph.DFormParagraphId === this.selectedCfgFormParagraph.DFormParagraphId) {
        const pendingDeletions = (paragraph.cfgDFormItems || []).filter(item => item.xauditDeleted);

        paragraph.cfgDFormItems = [];
        const liveItemIds = new Set<string>();
        this.selectedParagraphOrganizer.forEach((row, i) => {
          row.forEach((item: FormItemConfigurationModel, j) => {
            item.ParagraphColumn = j;
            item.ParagraphRow = i;
            item.ItemOrder = j;
            liveItemIds.add(item.DFormItemId);
            paragraph.cfgDFormItems.push(item);
          });
        });

        // Re-append the tombstones, parked out of the live grid so they can never collide with a
        // real cell (same convention as handleItemDelete). An id already present as a live item
        // means the deletion was undone, so skip it.
        pendingDeletions
          .filter(item => !liveItemIds.has(item.DFormItemId))
          .forEach(item => {
            item.ParagraphColumn = 99999;
            item.ItemOrder = 99999;
            paragraph.cfgDFormItems.push(item);
          });
      }
    });
  }

  visibleParagraphs(): FormParagraphConfigurationModel[] {
    return (this.selectedCfgFormVersion?.cfgDFormParagraphs || []).filter(p => !p.xauditDeleted);
  }

  filterOutAllDeletedObjects(): void {
    if (this.selectedCfgFormVersion) {
      this.selectedCfgFormVersion.cfgDFormParagraphs = this.selectedCfgFormVersion.cfgDFormParagraphs.filter(
        p => !p.xauditDeleted
      );
    }
    if (this.selectedCfgFormParagraph) {
      this.selectedCfgFormParagraph.cfgDFormItems = this.selectedCfgFormParagraph.cfgDFormItems.filter(
        item => !item.xauditDeleted
      );
    }
  }

  saveForm(form: FormConfigurationModel): Promise<any> {
    return new Promise((resolve, reject) => {
      const formClone = JSON.parse(JSON.stringify(form));
      formClone.cfgDFormVersions = [];
      formClone.enumFormCategory = null;
      formClone.enumFormDataSource = null;
      formClone.enumCustomForm = null;

      this.builderService.saveCfgDForm(formClone)
        .subscribe(
          (savedForm: FormConfigurationModel) => {
            // Sync backend-assigned DFormId (backend replaces client GUID on INSERT)
            if (savedForm && savedForm.DFormId !== form.DFormId) {
              const oldId = form.DFormId;
              this.selectedCfgForm.DFormId = savedForm.DFormId;
              if (this.selectedCfgFormVersion && this.selectedCfgFormVersion.DFormId === oldId) {
                this.selectedCfgFormVersion.DFormId = savedForm.DFormId;
              }
            }
            resolve(savedForm);
          },
          err => reject(err)
        );
    });
  }

  saveVersion(version: FormVersionConfigurationModel): Promise<any> {
    return new Promise((resolve, reject) => {
      const versionClone = JSON.parse(JSON.stringify(version));
      versionClone.cfgDFormParagraphs = [];
      versionClone.enumDFormBuildStatus = null;

      this.builderService.saveCfgFormVersion(versionClone)
        .subscribe(response => resolve(response), err => reject(err));
    });
  }

  saveParagraph(paragraph: FormParagraphConfigurationModel): Promise<any> {
    return new Promise((resolve, reject) => {
      const paragraphClone = JSON.parse(JSON.stringify(paragraph));
      paragraphClone.RepeatDFormItem = null;
      paragraphClone.cfgDFormItems = [];
      paragraphClone.enumParagraphOrientation = null;
      paragraphClone.enumItemLayout = null;

      this.builderService.saveCfgParagraph(paragraphClone)
        .subscribe(response => resolve(response), err => reject(err));
    });
  }

  saveItem(item: FormItemConfigurationModel, form: FormConfigurationModel): Promise<any> {
    return new Promise((resolve, reject) => {
      const itemClone = JSON.parse(JSON.stringify(item));
      itemClone.Calculation = null;
      itemClone.BeforeDisplayItemAction = null;
      itemClone.AfterUpdateItemAction = null;
      itemClone.ValidValue = null;
      itemClone.ChildDFormItems = [];
      itemClone.cfgDFormMasterItem = null;
      itemClone.enumFormControl = null;
      itemClone.enumLabelAligment = null;
      itemClone.enumPostLabelAligment = null;

      if (!itemClone.DFormMasterItemId) {
        this.createNewMasterItem(itemClone, form)
          .then((masterItem: FormMasterItemConfigurationModel) => {
            item.DFormMasterItemId = masterItem.DFormMasterItemId;
            itemClone.DFormMasterItemId = masterItem.DFormMasterItemId;
            this.builderService.saveCfgItem(itemClone)
              .subscribe(response => resolve(response), err => reject(err));
          })
          .catch(err => reject(err));
      } else {
        this.builderService.saveCfgItem(itemClone)
          .subscribe(response => resolve(response), err => reject(err));
      }
    });
  }

  createNewMasterItem(item: FormItemConfigurationModel, form: FormConfigurationModel): Promise<FormMasterItemConfigurationModel> {
    return new Promise((resolve, reject) => {
      const newMasterItem = new FormMasterItemConfigurationModel();

      this.getNewGuids(1)
        .then((guids: string[]) => {
          newMasterItem.DFormMasterItemId = guids[0];
          newMasterItem.TDOCID = this.TDOCID;
          newMasterItem.DFormItemName = `${item.DFormItemName}-${form.DFormName}-${guids[0].slice(0, 10)}`;
          newMasterItem.enumFormControlID = item.enumFormControlID;
          newMasterItem.Label = item.Label;
          newMasterItem.xauditDeleted = false;
          newMasterItem.xauditModifiedOn = new Date();
          newMasterItem.xauditCreatedOn = new Date();

          return this.saveMasterItem(newMasterItem);
        })
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  saveMasterItem(masterItem: FormMasterItemConfigurationModel): Promise<FormMasterItemConfigurationModel> {
    return new Promise((resolve, reject) => {
      const masterClone = JSON.parse(JSON.stringify(masterItem));
      masterClone.enumFormControl = null;

      this.builderService.saveCfgMasterItem(masterClone)
        .subscribe(response => resolve(response), err => reject(err));
    });
  }

  getSpoofDataForItem(cfgItem: FormItemConfigurationModel): FormItemDataModel {
    const key = cfgItem.DFormItemId;
    if (!key) {
      return this.stateService.createSpoofItemData(cfgItem, undefined, this.validValues);
    }
    if (!this.spoofDataCache.has(key) || this.spoofDataDirty) {
      this.spoofDataCache.set(key, this.stateService.createSpoofItemData(cfgItem, undefined, this.validValues));
    }
    return this.spoofDataCache.get(key);
  }

  invalidateSpoofCache(): void {
    this.spoofDataCache.clear();
    this.spoofDataDirty = true;
    // Reset dirty flag after one cycle so cache is rebuilt
    setTimeout(() => { this.spoofDataDirty = false; }, 0);
  }

  onItemClick(item: FormItemConfigurationModel, rowIndex: number, itemIndex: number, event: Event): void {
    event.stopPropagation();
    if (this.selectedItemId === item.DFormItemId) {
      // Double-click behavior: open detail dialog
      this.openItemDetailDetail(item, rowIndex, itemIndex);
    } else {
      this.selectedItemId = item.DFormItemId;
    }
  }

  /**
   * Switches the center pane between the whole-form preview and the paragraph builder.
   *
   * Going to preview flushes the organizer first (overwriteParagraphItems), otherwise in-progress
   * row/item edits would be missing from the render. Going to edit picks the first paragraph when
   * nothing is selected, so Edit never lands on an empty pane while the form has content.
   */
  setCenterMode(mode: 'preview' | 'edit'): void {
    if (mode === this.centerMode) { return; }

    if (mode === 'preview') {
      if (this.selectedParagraphOrganizer) {
        this.overwriteParagraphItems();
      }
      this.centerMode = 'preview';
      this.refreshCenterPreview();
      return;
    }

    this.centerMode = 'edit';
    if (!this.selectedCfgFormParagraph) {
      const first = this.visibleParagraphs()[0];
      if (first) {
        this.selectDFormParagraph(first);
      }
    }
  }

  /**
   * Flips the embedded preview between the paper-page frame and the flat in-app document layout.
   * The preview component reacts through ngOnChanges on its `useDocumentFrame` input.
   */
  togglePreviewLayoutMode(): void {
    this.previewUseDocumentFrame = !this.previewUseDocumentFrame;
  }

  /**
   * Re-feeds the embedded preview. The clone is deliberate: FormsBuilderPolishedPreviewComponent
   * re-renders from ngOnChanges on `formVersion`, which only fires when the input reference
   * changes — reassigning the same selectedCfgFormVersion object would leave stale content on
   * screen. Skipped while editing, since nothing is rendering the preview then.
   */
  refreshCenterPreview(): void {
    if (!this.selectedCfgFormVersion || this.centerMode !== 'preview') {
      return;
    }
    // No explicit detectChanges: this component uses default change detection, and every caller
    // is either a click handler or a zone-patched async callback. Forcing it from
    // setupVersionData() would run before the view exists on the synchronous load path.
    this.previewFormVersion = JSON.parse(JSON.stringify(this.selectedCfgFormVersion));
  }

  // Legacy live preview (kept for compatibility with old side-panel toggle)
  toggleLivePreview(): void {
    this.livePreviewEnabled = !this.livePreviewEnabled;
    if (this.livePreviewEnabled && this.selectedCfgFormVersion) {
      this.syncLivePreview();
    }
  }

  syncLivePreview(): void {
    // In live preview mode, invalidate spoof cache instead of syncing a separate panel
    if (this.livePreviewMode) {
      this.invalidateSpoofCache();
      this.cdr.detectChanges();
      return;
    }

    if (!this.livePreviewEnabled || !this.selectedCfgFormVersion) return;

    if (this.previewUpdateTimer) {
      clearTimeout(this.previewUpdateTimer);
    }

    this.previewUpdateTimer = setTimeout(() => {
      if (this.selectedParagraphOrganizer) {
        this.overwriteParagraphItems();
      }
      this.livePreviewCfgFormVersion = null;
      this.cdr.detectChanges();
      this.livePreviewCfgFormVersion = JSON.parse(JSON.stringify(this.selectedCfgFormVersion));
      this.cdr.detectChanges();
    }, 350);
  }

  openPreview(): void {
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }

    this.dialog.open(FormsBuilderPolishedPreviewComponent, {
      width: '1500px',
      height: '95%',
      data: { cfgFormVersion: this.selectedCfgFormVersion }
    });
  }

  openTutorial(): void {
    this.dialog.open(FormsBuilderTutorialComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'tutorial-dialog',
      data: { startSection: 'editor' }
    });
  }

  /**
   * Opens the unified AI Form Studio (chat + live side-by-side preview). `entry` selects the
   * initial mode: 'pdf' shows the PDF upload step first; 'chat' goes straight to the prompt.
   * The Studio maintains its own working copy of the form (seeded from a clone of the live
   * version) and applies each AI turn to it via the applyDraftToWorkingVersion callback. On
   * accept, the whole working copy is committed to the live form in one swap.
   */
  openAiStudio(entry: 'pdf' | 'chat'): void {
    if (!this.formIsEditable || !this.selectedCfgFormVersion) {
      this.showMessage('Info', 'Open an editable form version to use the AI Form Studio.');
      return;
    }

    // Flush any pending organizer edits into the model before cloning the base working version.
    if (this.selectedParagraphOrganizer) {
      this.overwriteParagraphItems();
    }

    const dialogRef = this.dialog.open(FormsBuilderAiStudioDialogComponent, {
      width: '95vw',
      maxWidth: '98vw',
      height: '92vh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ai-studio-dialog-panel',
      data: {
        entry,
        catalog: this.buildAiCatalogSnapshot(),
        existingFormContext: {
          formName: this.selectedCfgForm?.DFormName,
          version: this.selectedCfgFormVersion?.Version,
          revision: this.selectedCfgFormVersion?.Revision
        },
        baseVersion: JSON.parse(JSON.stringify(this.selectedCfgFormVersion)),
        applyDraftToWorkingVersion: (
          base: FormVersionConfigurationModel,
          draft: AiGeneratedFormDraft,
          mode: 'pdf' | 'edit'
        ) => this.applyDraftToWorkingVersion(base, draft, mode),
        snapshotParagraphs: (version: FormVersionConfigurationModel) =>
          this.buildExistingParagraphSnapshot(version)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.applied || !result?.workingVersion) {
        return;
      }

      try {
        this.commitWorkingVersion(result.workingVersion as FormVersionConfigurationModel);
        // Telemetry gets its own catch: it writes to localStorage, so a full quota used to throw
        // here AFTER the paragraphs had already been applied and report "Failed to apply AI
        // changes." for an apply that in fact succeeded. Losing a usage counter is not worth
        // misreporting the outcome.
        if (result.appliedDraft) {
          try {
            this.recordAiDraftApplyTelemetry(result.appliedDraft as AiGeneratedFormDraft);
          } catch (telemetryError) {
            console.warn('Failed recording AI apply telemetry; changes were applied.', telemetryError);
          }
        }
        // The dialog only ever wrote to an in-memory working copy — nothing has touched the
        // backend yet. A 3s snackbar was too easy to miss for a change this destructive to lose,
        // so raise a banner that persists until the form is actually saved.
        this.aiChangesAwaitingSave = true;
        this.aiChangesAwaitingSaveMode = entry === 'pdf' ? 'import' : 'edit';
      } catch (error) {
        console.error('Failed applying AI Studio changes', error);
        this.showMessage('Error', 'Failed to apply AI changes.');
      }
    });
  }

  /**
   * Applies an AI draft to a throwaway clone of `base` using server-issued GUIDs, returning the
   * updated clone, the GUIDs of paragraphs that were added or replaced (for the Studio's change
   * highlight), and any warnings raised while applying. The input `base` is never mutated — the
   * Studio keeps it as the undo point. PDF mode soft-deletes all existing paragraphs first to
   * mirror the replace-all import behavior.
   */
  async applyDraftToWorkingVersion(
    base: FormVersionConfigurationModel,
    draft: AiGeneratedFormDraft,
    mode: 'pdf' | 'edit'
  ): Promise<{
    version: FormVersionConfigurationModel;
    changedIds: string[];
    applyWarnings: string[];
  }> {
    return this.aiDraftService.applyDraftToWorkingVersion(base, draft, mode, this.buildAiDraftContext());
  }

  /** Draft-application context wired to this editor's live state and telemetry hooks. */
  private buildAiDraftContext(): AiDraftApplyContext {
    return {
      validValues: this.validValues,
      templateFields: this.aiDraftService.flattenTemplateFields([
        ...(this.prebuiltTemplates || []),
        ...(this.customTemplateGroups || [])
      ]),
      getGuids: (n: number) => this.getNewGuids(n),
      isPreview: true,
      onControlUsed: (controlTypeId: string) => this.trackUsageByControlId(controlTypeId)
      // onLinkWarnings/onNamingWarnings are deliberately not wired. They used to raise a 3s
      // snackbar that pointed at the console — too short to act on, and the detail naming the
      // offending field was never on screen at all. Those warnings now come back from
      // applyDraftToWorkingVersion and land in the Studio's Review notes panel, which persists
      // for the whole session and lists each one in full.
    };
  }

  /**
   * Commits the Studio's finished working copy to the live form. The working copy is a clone of the
   * live version (same DFormVersionId) whose new paragraphs already carry server-issued GUIDs and
   * whose removed paragraphs keep their xauditDeleted markers for the save audit trail, so the
   * paragraph array can be swapped in wholesale.
   */
  private commitWorkingVersion(workingVersion: FormVersionConfigurationModel): void {
    this.selectedParagraphOrganizer = null;
    this.selectedCfgFormParagraph = null;
    this.selectedParagraphId = null;

    this.selectedCfgFormVersion.cfgDFormParagraphs = workingVersion.cfgDFormParagraphs || [];
    this.aiDraftService.recalculateParagraphOrder(this.selectedCfgFormVersion);

    const firstActive = (this.selectedCfgFormVersion.cfgDFormParagraphs || [])
      .filter(p => !p.xauditDeleted)
      .sort((a, b) => (a.ParagraphOrder || 0) - (b.ParagraphOrder || 0))[0];
    if (firstActive) {
      this.selectDFormParagraph(firstActive);
    }

    // selectDFormParagraph flips the center pane to edit. After an AI apply — especially a PDF
    // import that replaced every paragraph — the useful view is the whole generated form, not one
    // paragraph of it, so return to preview. The selection above is kept so Edit is ready to go.
    this.centerMode = 'preview';
    this.refreshCenterPreview();

    this.syncLivePreview();
  }

  private buildExistingParagraphSnapshot(
    version: FormVersionConfigurationModel = this.selectedCfgFormVersion
  ): AiExistingParagraphContext[] {
    return this.aiDraftService.buildExistingParagraphSnapshot(version);
  }

  private buildAiCatalogSnapshot(): AiFormCatalogSnapshot {
    return this.aiDraftService.buildCatalogSnapshot(
      [...(this.prebuiltTemplates || []), ...(this.customTemplateGroups || [])],
      this.validValues
    );
  }

  private trackUsageByControlId(controlTypeId: string): void {
    const fullItemType = FormsConstants.cfgFormItemTypeList.find(item => item.id === controlTypeId);
    if (fullItemType) {
      this.trackItemUsage(fullItemType);
    }
  }

  private recordAiDraftApplyTelemetry(draft: AiGeneratedFormDraft): void {
    const key = 'paragraphBuilder.aiApplyTelemetry';
    const itemCount = (draft?.paragraphs || []).reduce((total, paragraph) => {
      const rowItems = (paragraph.rows || []).reduce((rowTotal, row) => rowTotal + (row?.length || 0), 0);
      return total + rowItems;
    }, 0);

    let current: any = { applies: 0, paragraphs: 0, items: 0 };
    try {
      current = JSON.parse(localStorage.getItem(key) || '{"applies":0,"paragraphs":0,"items":0}');
    } catch {
      current = { applies: 0, paragraphs: 0, items: 0 };
    }

    current.applies = (current.applies || 0) + 1;
    current.paragraphs = (current.paragraphs || 0) + (draft?.paragraphs?.length || 0);
    current.items = (current.items || 0) + itemCount;
    current.lastAppliedOn = new Date().toISOString();

    localStorage.setItem(key, JSON.stringify(current));
  }

  // Item filtering
  filterItems(): void {
    const query = this.itemSearchQuery.toLowerCase().trim();

    if (!query) {
      this.filteredCategorizedItems = this.categorizedItems;
      return;
    }

    const filtered = new Map<FormItemCategory, FormItemType[]>();

    this.categorizedItems.forEach((items, category) => {
      const matchingItems = items.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.aliases?.some(alias => alias.toLowerCase().includes(query))
      );
      if (matchingItems.length > 0) {
        filtered.set(category, matchingItems);
      }
    });

    this.filteredCategorizedItems = filtered;
  }

  clearItemSearch(): void {
    this.itemSearchQuery = '';
    this.filteredCategorizedItems = this.categorizedItems;
  }

  organizeItemTypeList(): void {
    // Called when item list drops
    this.consentFormItems = this.consentFormItems.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    this.otherItems = this.otherItems.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    this.cfgFormItemTypeList = [...this.consentFormItems, ...this.otherItems];
    this.filteredConsentFormItems = this.consentFormItems;
    this.filteredOtherItems = this.otherItems;
  }

  // Tab management
  onParagraphTabChange(index: number): void {
    this.paragraphTabIndex = index;
    const paragraphs = this.selectedCfgFormVersion?.cfgDFormParagraphs;
    if (index === 1 && !this.selectedCfgFormParagraph && paragraphs?.length > 0) {
      this.selectDFormParagraph(paragraphs[0]);
    }
  }

  getTotalItemCount(): number {
    if (!this.selectedParagraphOrganizer) return 0;
    return this.selectedParagraphOrganizer.reduce((total, row) => {
      const nonDeletedItems = row.filter((item: FormItemConfigurationModel) => !item.xauditDeleted);
      return total + nonDeletedItems.length;
    }, 0);
  }

  selectCfgFormVersionStatus(status: any): void {
    this.activeFormVersionStatus = status.id;
    this.selectedCfgFormVersion.enumDFormBuildStatus = null;
    this.selectedCfgFormVersion.enumDFormBuildStatusID = status.id;
  }

  getVersionStatus(statusId: string): any {
    return this.cfgFormVersionStatusList?.find(s => s.id?.toLowerCase() === statusId?.toLowerCase()) || null;
  }

  // Helper methods
  getNewGuids(count: number): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (this.guidList.length >= count) {
        resolve(this.guidList.splice(0, count));
      } else {
        this.builderService.getNewGuids(500)
          .subscribe(
            response => {
              this.guidList = this.guidList.concat(response);
              resolve(this.guidList.splice(0, count));
            },
            err => reject(err)
          );
      }
    });
  }

  fetchAllValidValues(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.builderService.getAllValidValues()
        .subscribe(response => resolve(response), err => reject(err));
    });
  }

  fetchAllActions(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.builderService.getAllActions()
        .subscribe(response => resolve(response), err => reject(err));
    });
  }

  confirm(message: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(FormsBuilderPolishedConfirmComponent, {
        width: '500px',
        height: '225px',
        data: { message }
      });
      dialogRef.afterClosed().subscribe(
        result => resolve(result),
        err => reject(err)
      );
    });
  }

  showMessage(type: string, message: string): void {
    this.snackBar.open(message, type, {
      duration: 3000,
      verticalPosition: 'top'
    });
  }

  // Clear item selection when the user clicks anywhere outside a builder item.
  // Item and toolbar clicks call stopPropagation, so they never reach this.
  @HostListener('document:click')
  clearItemSelection(): void {
    this.selectedItemId = null;
  }

  // Keyboard shortcuts
  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      if (this.selectedCfgForm && this.formIsEditable) {
        this.saveFormData();
      }
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
      event.preventDefault();
      if (this.selectedCfgFormVersion && this.formIsEditable) {
        this.createNewDFormParagraph();
      }
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
      event.preventDefault();
      if (this.selectedCfgFormVersion) {
        this.openPreview();
      }
    }

    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'F') {
      event.preventDefault();
      const searchInput = document.querySelector('[data-cy="paragraph-builder-item-search-input"]') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }

    // Close expanded text editor on Escape
    if (event.key === 'Escape' && this.expandedItemId) {
      event.preventDefault();
      this.collapseItem();
    }
  }

  getKeyboardShortcutsTooltip(): string {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modKey = isMac ? 'Cmd' : 'Ctrl';
    return `Keyboard Shortcuts:\n${modKey}+S - Save\n${modKey}+N - New paragraph\n${modKey}+P - Preview\n${modKey}+Shift+F - Search items`;
  }

  // Inline editing methods
  isInlineEditable(item: FormItemConfigurationModel): boolean {
    return this.inlineEditableControlTypes.includes(item.enumFormControlID);
  }

  isMultilineControl(item: FormItemConfigurationModel): boolean {
    return this.multilineControlTypes.includes(item.enumFormControlID);
  }

  getPlaceholderForType(item: FormItemConfigurationModel): string {
    const typeLabel = this.getItemLabel(item);
    return `Enter ${typeLabel.name?.toLowerCase() || 'text'}...`;
  }

  onInlineEditBlur(item: FormItemConfigurationModel): void {
    this.syncLivePreview();
  }

  onInlineEditEnter(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  // Expanded text editing methods
  isItemExpanded(item: FormItemConfigurationModel): boolean {
    return this.expandedItemId === item.DFormItemId;
  }

  toggleItemExpanded(item: FormItemConfigurationModel, event: Event): void {
    event.stopPropagation();
    if (this.expandedItemId === item.DFormItemId) {
      this.expandedItemId = null;
    } else {
      this.expandedItemId = item.DFormItemId;
    }
  }

  collapseItem(): void {
    this.expandedItemId = null;
    this.syncLivePreview();
  }

  hasLongText(item: FormItemConfigurationModel): boolean {
    return (item.Label?.length || 0) > 50;
  }

  getExpandedItem(): FormItemConfigurationModel | null {
    if (!this.expandedItemId) return null;
    for (const row of this.selectedParagraphOrganizer || []) {
      const item = row.find((i: FormItemConfigurationModel) => i.DFormItemId === this.expandedItemId);
      if (item) return item;
    }
    return null;
  }

  // Quick actions
  toggleRequired(item: FormItemConfigurationModel): void {
    item.isRequired = !item.isRequired;
    this.syncLivePreview();
  }

  quickDuplicate(item: FormItemConfigurationModel, rowIndex: number): void {
    this.duplicateItem(item, rowIndex);
    this.syncLivePreview();
  }

  // Auto-row creation
  dropEventAutoCreateRow(event: CdkDragDrop<any[]>): void {
    // Create new row and add item to it
    this.selectedParagraphOrganizer.push([]);
    const newRowIndex = this.selectedParagraphOrganizer.length - 1;
    this.rebuildConnectedDropLists();

    // Use event.item.data when available (set via cdkDragData), otherwise fallback to container index
    const itemToMove = event.item.data ?? event.previousContainer.data[event.previousIndex];

    if (this.isPrebuiltTemplate(itemToMove)) {
      this.addPrebuiltItemToRow(this.selectedParagraphOrganizer[newRowIndex], itemToMove, newRowIndex);
    } else if (this.isItemTypeDefinition(itemToMove)) {
      this.addEmptyItemIntoRow(this.selectedParagraphOrganizer[newRowIndex], itemToMove, newRowIndex);
    } else if (this.isFormItem(itemToMove)) {
      transferArrayItem(
        event.previousContainer.data,
        this.selectedParagraphOrganizer[newRowIndex],
        event.previousIndex,
        0
      );
      this.formatFormItemsFromOrganizer();
    }
    this.syncLivePreview();
  }

  // Prebuilt field templates
  addPrebuiltItemToRow(row: any[], template: PrebuiltTemplate, rowIndex: number, insertIndex: number = -1): void {
    // Track usage for recently used list
    const fullItemType = FormsConstants.cfgFormItemTypeList.find(item => item.id === template.controlType);
    if (fullItemType) {
      this.trackItemUsage(fullItemType);
    }

    // Composite templates insert a Label item + an Input item side by side
    if (template.composite) {
      this.addCompositePrebuiltItems(row, template, rowIndex, insertIndex);
      return;
    }

    const newItem = new FormItemConfigurationModel();
    newItem.DFormParagraphId = this.selectedCfgFormParagraph.DFormParagraphId;
    newItem.enumFormControlID = template.controlType;
    newItem.xauditDeleted = false;
    newItem.DFormItemName = template.name;
    newItem.Label = template.label;
    if (template.defaultValue) {
      newItem.DefaultValue = template.defaultValue;
    }

    if (template.validValueName) {
      const validValue = this.validValues?.find((v: any) => v.ValidValueName === template.validValueName);
      if (validValue) {
        newItem.ValidValueId = validValue.ValidValueId;
        newItem.ValidValue = JSON.parse(JSON.stringify(validValue));
      } else {
        console.warn(`Valid value not found: "${template.validValueName}"`);
        console.log('Available valid values:', this.validValues?.map(v => v.ValidValueName));
      }
    }

    this.getNewGuids(1)
      .then((guids: string[]) => {
        newItem.DFormItemId = guids[0];
        if (insertIndex >= 0 && insertIndex < row.length) {
          row.splice(insertIndex, 0, newItem);
        } else {
          row.push(newItem);
        }
        this.rebuildConnectedDropLists();
        this.formatFormItemsFromOrganizer();
        this.syncLivePreview();
      })
      .catch(err => {
        console.error('Failed to generate GUID for prebuilt item:', err);
        this.showMessage('Error', 'Failed to add field. Please try again.');
      });
  }

  private addCompositePrebuiltItems(row: any[], template: PrebuiltTemplate, rowIndex: number, insertIndex: number = -1): void {
    const paragraphId = this.selectedCfgFormParagraph.DFormParagraphId;

    // Label item (Paragraph control type)
    const labelItem = new FormItemConfigurationModel();
    labelItem.DFormParagraphId = paragraphId;
    labelItem.enumFormControlID = '00000001-1000-0000-0000-000000000006'; // Paragraph/Label
    labelItem.xauditDeleted = false;
    labelItem.DFormItemName = template.name + ' Label';
    labelItem.Label = template.label + ':';
    labelItem.ParagraphColumnWidth = template.labelWidth ?? 30;

    // Input item (original control type)
    const inputItem = new FormItemConfigurationModel();
    inputItem.DFormParagraphId = paragraphId;
    inputItem.enumFormControlID = template.controlType;
    inputItem.xauditDeleted = false;
    inputItem.DFormItemName = template.name;
    inputItem.Label = '';
    inputItem.ParagraphColumnWidth = template.inputWidth ?? 70;

    if (template.validValueName) {
      const validValue = this.validValues?.find((v: any) => v.ValidValueName === template.validValueName);
      if (validValue) {
        inputItem.ValidValueId = validValue.ValidValueId;
        inputItem.ValidValue = JSON.parse(JSON.stringify(validValue));
      } else {
        console.warn(`Valid value not found: "${template.validValueName}"`);
        console.log('Available valid values:', this.validValues?.map(v => v.ValidValueName));
      }
    }

    this.getNewGuids(2)
      .then((guids: string[]) => {
        labelItem.DFormItemId = guids[0];
        inputItem.DFormItemId = guids[1];

        if (insertIndex >= 0 && insertIndex < row.length) {
          row.splice(insertIndex, 0, labelItem, inputItem);
        } else {
          row.push(labelItem, inputItem);
        }
        this.rebuildConnectedDropLists();
        this.formatFormItemsFromOrganizer();
        this.syncLivePreview();
      })
      .catch(err => {
        console.error('Failed to generate GUIDs for composite prebuilt items:', err);
        this.showMessage('Error', 'Failed to add field. Please try again.');
      });
  }

  addPrebuiltToCurrentRow(template: PrebuiltTemplate): void {
    if (!this.selectedParagraphOrganizer || !this.formIsEditable) return;

    // Add to last row, or create new row if none exist
    let targetRowIndex = this.selectedParagraphOrganizer.length - 1;
    if (targetRowIndex < 0) {
      this.selectedParagraphOrganizer.push([]);
      this.rebuildConnectedDropLists();
      targetRowIndex = 0;
    }

    this.addPrebuiltItemToRow(this.selectedParagraphOrganizer[targetRowIndex], template, targetRowIndex);
  }

  // Custom prebuilt template methods

  loadCustomTemplates(): void {
    this.customTemplateGroups = this.aiDraftService.loadCustomTemplateGroups();
  }

  private saveCustomTemplates(): void {
    localStorage.setItem('paragraphBuilder.customTemplates', JSON.stringify(this.customTemplateGroups));
  }

  saveSelectionAsTemplate(): void {
    if (!this.selectedParagraphOrganizer || this.selectedParagraphOrganizer.length === 0) return;

    const name = window.prompt('Enter a name for this template:');
    if (!name || !name.trim()) return;

    const fields: PrebuiltTemplate[] = [];

    for (const row of this.selectedParagraphOrganizer) {
      for (const item of row) {
        if (item.xauditDeleted) continue;

        const itemType = FormsConstants.cfgFormItemTypeList.find(t => t.id === item.enumFormControlID);
        const field: PrebuiltTemplate = {
          name: item.DFormItemName || itemType?.name || 'Field',
          label: item.Label || '',
          controlType: item.enumFormControlID,
          icon: itemType?.icon || 'text_fields',
        };

        if (item.ValidValue?.ValidValueName) {
          field.validValueName = item.ValidValue.ValidValueName;
        }
        if (item.DefaultValue) {
          field.defaultValue = item.DefaultValue;
        }

        fields.push(field);
      }
    }

    if (fields.length === 0) {
      this.showMessage('No items', 'The current paragraph has no items to save as a template.');
      return;
    }

    const newGroup: CustomPrebuiltGroup = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2),
      category: name.trim(),
      icon: 'bookmark',
      fields: fields,
      createdOn: new Date().toISOString(),
    };

    this.customTemplateGroups.push(newGroup);
    this.saveCustomTemplates();
    this.showMessage('Template saved', `"${name.trim()}" saved with ${fields.length} field(s).`);
  }

  deleteCustomTemplate(group: CustomPrebuiltGroup): void {
    const confirmed = window.confirm(`Delete template "${group.category}"?`);
    if (!confirmed) return;

    this.customTemplateGroups = this.customTemplateGroups.filter(g => g.id !== group.id);
    this.saveCustomTemplates();
    this.showMessage('Template deleted', `"${group.category}" has been removed.`);
  }
}

